import React, { useState, useCallback, useEffect } from "react";
import styles from "./SuscriptionModalNew.module.scss";
import { motion } from "framer-motion";
import { connect } from "react-redux";
import { Payment } from "@mercadopago/sdk-react";
import ModalOverlay from "../ModalOverlay/ModalOverlay.tsx";
import {
  closeSuscriptionModal,
  getSuscriptionLink,
  loadUserData,
  setSuscriptionLoading,
} from "../../../store/actions";
import Loading from "../Loading/Loading";
import { fireAnalytics } from "../../../utils/firebase";
import { getSessionId, getDeviceCategory } from "../../../utils/analyticsHelpers.ts";
import { 
  CheckCircle, 
  XCircle, 
  Check, 
  X, 
  CreditCard, 
  Building2,
  ChevronDown,
  ChevronUp,
  Sparkles
} from "lucide-react";

// Componente separado para el formulario de MercadoPago
interface MpFormProps {
  selectedPlan: typeof PLANS[0];
  onStartPayment: (paymentData: any) => Promise<void>;
  onReady: () => void;
}

const MpForm: React.FC<MpFormProps> = React.memo(({ selectedPlan, onStartPayment, onReady }) => {
  const validateAmount = (amount: number) => {
    if (amount < 1) return false;
    if (amount > 100000) return false;
    return true;
  };

  if (!validateAmount(selectedPlan.price)) {
    return (
      <div className={styles.cardPayment}>
        <div className={styles.paymentError}>
          <XCircle size={48} />
          <p>Monto inválido para el pago</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.cardPayment}>
      <Payment
        initialization={{ 
          amount: selectedPlan.price
        }}
        customization={{
          paymentMethods: {
            creditCard: "all",
            debitCard: "all",
          },
        }}
        onReady={onReady}
        onSubmit={({ selectedPaymentMethod, formData }) => {
          
          if (selectedPaymentMethod.includes('credit') || selectedPaymentMethod.includes('debit')) {
            const cardData = {
              ...formData,
              issuer_id: Number(formData.issuer_id),
              description: `Suscripcion Edify - ${selectedPlan.name}`,
            };
            
            return onStartPayment(cardData);
          }
          
          return Promise.resolve();
        }}
        onError={(error) => {
          console.error('Payment error:', error);
        }}
      />
    </div>
  );
});

MpForm.displayName = 'MpForm';

// Definición de features
const FEATURES = [
  { id: 1, label: 'Acceso a la plataforma' },
  { id: 2, label: 'Publicaciones ilimitadas de obras privadas' },
  { id: 3, label: 'Publicaciones ilimitadas de artículos en el muro' },
  { id: 4, label: 'Postulaciones para cotizar en proyectos existentes' }
];

// Definición de planes
const PLANS = [
  {
    id: "0",
    name: "Básico",
    price: 0,
    period: "Gratis",
    discount: null,
    features: [1],
    isFree: true
  },
  {
    id: "1",
    name: "Mensual",
    price: 1500,
    period: "mes",
    discount: null,
    features: [1, 2, 3, 4],
    isPremium: true
  },
  {
    id: "2",
    name: "Semestral",
    price: 6000,
    period: "6 meses",
    discount: "20% OFF",
    features: [1, 2, 3, 4],
    isPremium: true
  },
  {
    id: "3",
    name: "Anual",
    price: 10800,
    period: "año",
    discount: "40% OFF",
    features: [1, 2, 3, 4],
    isPremium: true,
    isRecommended: true
  }
];

interface SuscriptionModalNewProps {
  userData: any;
  suscriptionLoading: boolean;
  isUserPremium: boolean;
  closeSuscriptionModal: () => void;
  getSuscriptionLink: (planId: string, userId: number, cardData: any, onSuccess: () => void, onError: (code: number) => void) => void;
  loadUserData: () => void;
  setSuscriptionLoading: () => void;
}

type ModalView = 'plans' | 'payment' | 'transfer' | 'result';

const modalVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.3 }
  }
};

// Componente de tarjeta de plan
const PlanCard: React.FC<{
  plan: typeof PLANS[0];
  isCurrentPlan: boolean;
  onSelectPayment: (planId: string) => void;
  onSelectTransfer: (planId: string, amount: number) => void;
  isExpanded: boolean;
  onToggleExpand: () => void;
}> = ({ plan, isCurrentPlan, onSelectPayment, onSelectTransfer, isExpanded, onToggleExpand }) => {
  return (
    <div className={`${styles.planCard} ${plan.isRecommended ? styles.recommended : ''} ${isCurrentPlan ? styles.current : ''}`}>
      {plan.isRecommended && (
        <div className={styles.badge}>
          <Sparkles size={14} />
          Recomendado
        </div>
      )}
      {isCurrentPlan && (
        <div className={styles.currentBadge}>
          Plan Actual
        </div>
      )}
      
      <div className={styles.planHeader}>
        <h3 className={styles.planName}>{plan.name}</h3>
        <div className={styles.planPrice}>
          <span className={styles.currency}>$</span>
          <span className={styles.amount}>{plan.price.toLocaleString('es-AR')}</span>
          {plan.period !== 'Gratis' && (
            <span className={styles.period}>/ {plan.period}</span>
          )}
        </div>
        {plan.discount && (
          <div className={styles.discount}>{plan.discount}</div>
        )}
      </div>

      <div className={styles.planFeatures}>
        {FEATURES.map(feature => {
          const hasFeature = plan.features.includes(feature.id);
          return (
            <div key={feature.id} className={styles.feature}>
              {hasFeature ? (
                <Check size={18} className={styles.checkIcon} />
              ) : (
                <X size={18} className={styles.xIcon} />
              )}
              <span className={hasFeature ? styles.included : styles.notIncluded}>
                {feature.label}
              </span>
            </div>
          );
        })}
      </div>

      {!plan.isFree && (
        <div className={styles.planActions}>
          <button 
            className={styles.selectButton}
            onClick={onToggleExpand}
          >
            Elegir medio de pago
            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          
          {isExpanded && (
            <div className={styles.paymentOptions}>
              <button 
                className={styles.paymentOption}
                onClick={() => onSelectPayment(plan.id)}
              >
                <CreditCard size={18} />
                Tarjeta de crédito/débito
              </button>
              <button 
                className={styles.paymentOption}
                onClick={() => onSelectTransfer(plan.id, plan.price)}
              >
                <Building2 size={18} />
                Transferencia bancaria
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Componente de resultado
const ResultView: React.FC<{
  isSuccess: boolean;
  isMercadopagoError: boolean;
  onClose: () => void;
}> = ({ isSuccess, isMercadopagoError, onClose }) => {
  return (
    <div className={styles.resultContainer}>
      <div className={styles.resultIcon}>
        {isSuccess ? (
          <CheckCircle size={64} className={styles.successIcon} />
        ) : (
          <XCircle size={64} className={styles.errorIcon} />
        )}
      </div>
      
      <h2 className={styles.resultTitle}>
        {isSuccess ? '¡Pago exitoso!' : 'Error en el pago'}
      </h2>
      
      <p className={styles.resultMessage}>
        {isSuccess ? (
          'Ahora puedes acceder a las funcionalidades premium. Te notificaremos por email cuando el pago se haya efectivizado.'
        ) : isMercadopagoError ? (
          'La suscripción no se pudo crear correctamente. Inténtalo nuevamente o escríbenos a contacto@edify.la'
        ) : (
          'Verifica que los datos de la tarjeta sean correctos e inténtalo nuevamente. O escríbenos a contacto@edify.la'
        )}
      </p>

      <button className={styles.closeButton} onClick={onClose}>
        Cerrar
      </button>
    </div>
  );
};

// Componente de transferencia
const TransferView: React.FC<{
  amount: number;
  onBack: () => void;
}> = ({ amount, onBack }) => {
  return (
    <div className={styles.transferContainer}>
      <h2 className={styles.transferTitle}>Datos para transferencia</h2>
      
      <div className={styles.transferCard}>
        <div className={styles.transferLogo}>
          <Building2 size={48} />
        </div>
        <div className={styles.transferData}>
          <p><strong>Cuenta de Mercado Pago</strong></p>
          <p>CVU: 0000003100016478555577</p>
          <p>Alias: edify-web</p>
          <p>CUIT: 30-71680733-5</p>
        </div>
      </div>

      <div className={styles.transferAmount}>
        <p>Monto a transferir: <strong>$ {amount.toLocaleString('es-AR')}</strong></p>
      </div>

      <div className={styles.transferInstructions}>
        <p>Una vez realizada la transferencia, envíanos el comprobante a:</p>
        <a href="mailto:pagosmp@edify.la" className={styles.email}>
          pagosmp@edify.la
        </a>
        <p>Te enviaremos un email con la confirmación de tu suscripción.</p>
        <p>¿Dudas? Escríbenos al WhatsApp: <strong>11 6858-0968</strong></p>
      </div>

      <button className={styles.backButton} onClick={onBack}>
        Volver a planes
      </button>
    </div>
  );
};

const SuscriptionModalNew: React.FC<SuscriptionModalNewProps> = ({
  userData,
  suscriptionLoading,
  isUserPremium,
  closeSuscriptionModal,
  getSuscriptionLink,
  loadUserData,
  setSuscriptionLoading
}) => {
  const [currentView, setCurrentView] = useState<ModalView>('plans');
  const [selectedPlan, setSelectedPlan] = useState<typeof PLANS[0] | null>(null);
  const [expandedPlan, setExpandedPlan] = useState<string | null>(null);
  const [transferAmount, setTransferAmount] = useState(0);
  const [paymentResult, setPaymentResult] = useState<{
    isSuccess: boolean;
    isMercadopagoError: boolean;
  } | null>(null);

  useEffect(() => {
    if (currentView === 'plans') {
      fireAnalytics("subscription_modal_viewed", {
        edify_user_id: userData?.id || "",
        session_id: getSessionId(),
        device_category: getDeviceCategory(),
        category: "subscription",
        action: "modal_viewed",
        is_user_premium: isUserPremium || false
      });
    }
  }, [currentView, userData?.id, isUserPremium]);

  const handleSelectPayment = (planId: string) => {
    const plan = PLANS.find(p => p.id === planId);
    if (plan) {
      setSelectedPlan(plan);
      setCurrentView('payment');
      setSuscriptionLoading();
      
      fireAnalytics("subscription_plan_payment_selected", {
        edify_user_id: userData?.id || "",
        session_id: getSessionId(),
        device_category: getDeviceCategory(),
        category: "subscription",
        action: "plan_payment_selected",
        plan_id: planId,
        plan_name: plan.name,
        plan_price: plan.price,
        payment_method: "card"
      });
    }
  };

  const handleSelectTransfer = (planId: string, amount: number) => {
    const plan = PLANS.find(p => p.id === planId);
    setTransferAmount(amount);
    setCurrentView('transfer');
    
    fireAnalytics("subscription_plan_transfer_selected", {
      edify_user_id: userData?.id || "",
      session_id: getSessionId(),
      device_category: getDeviceCategory(),
      category: "subscription",
      action: "plan_transfer_selected",
      plan_id: planId,
      plan_name: plan?.name || "",
      plan_price: amount,
      payment_method: "transfer"
    });
  };

  const handleStartPayment = useCallback((cardData: any): Promise<void> => {
    
    if (!selectedPlan) {
      return Promise.resolve();
    }

    return new Promise<void>((resolve) => {
      const onSuccess = () => {
        fireAnalytics("subscription_payment_success", {
          edify_user_id: userData?.id || "",
          session_id: getSessionId(),
          device_category: getDeviceCategory(),
          category: "subscription",
          action: "payment_success",
          plan_id: selectedPlan.id,
          plan_name: selectedPlan.name,
          plan_price: selectedPlan.price,
          payment_method: "card"
        });
        setPaymentResult({ isSuccess: true, isMercadopagoError: false });
        setCurrentView('result');
        resolve(undefined);
      };

      const onError = (errorCode: number) => {
        const isMercadopagoError = errorCode >= 500;
        fireAnalytics("subscription_payment_failed", {
          edify_user_id: userData?.id || "",
          session_id: getSessionId(),
          device_category: getDeviceCategory(),
          category: "subscription",
          action: "payment_failed",
          plan_id: selectedPlan.id,
          plan_name: selectedPlan.name,
          plan_price: selectedPlan.price,
          payment_method: "card",
          error_code: errorCode,
          is_mercadopago_error: isMercadopagoError
        });
        setPaymentResult({
          isSuccess: false,
          isMercadopagoError
        });
        setCurrentView('result');
        resolve(undefined);
      };

      getSuscriptionLink(
        selectedPlan.id,
        userData?.id,
        cardData,
        onSuccess,
        onError
      );
    });
  }, [selectedPlan, userData?.id, getSuscriptionLink]);

  const handlePaymentReady = useCallback(() => {
    setSuscriptionLoading();
  }, [setSuscriptionLoading]);

  const handleClose = () => {
    if (paymentResult?.isSuccess) {
      loadUserData();
    }
    closeSuscriptionModal();
  };

  const handleBackToPlans = () => {
    setCurrentView('plans');
    setSelectedPlan(null);
    setExpandedPlan(null);
  };

  const isCurrentPlan = (planId: string) => {
    if (planId === "0") {
      return userData.free_trial?.status === "terminate" && !isUserPremium;
    }
    return false;
  };

  return (
    <>
      <ModalOverlay isOpen={true} onClose={handleClose}>
        <motion.div
          className={styles.modal}
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
        >
          {currentView === 'plans' && (
            <>
              <div className={styles.header}>
                <div className={styles.headerContent}>
                  <h2 className={styles.title}>Contenido Premium</h2>
                  <p className={styles.subtitle}>Elige el plan que mejor se adapte a tus necesidades</p>
                </div>
                <button className={styles.closeBtn} onClick={handleClose}>
                  <X size={20} />
                </button>
              </div>

              <div className={styles.plansGrid}>
                {PLANS.map(plan => (
                  <PlanCard
                    key={plan.id}
                    plan={plan}
                    isCurrentPlan={isCurrentPlan(plan.id)}
                    onSelectPayment={handleSelectPayment}
                    onSelectTransfer={handleSelectTransfer}
                    isExpanded={expandedPlan === plan.id}
                    onToggleExpand={() => setExpandedPlan(expandedPlan === plan.id ? null : plan.id)}
                  />
                ))}
              </div>

              <div className={styles.footer}>
                <p className={styles.footerNote}>
                  * Los precios se ajustarán trimestralmente por el índice CAC
                </p>
              </div>
            </>
          )}

          {currentView === 'payment' && selectedPlan && (
            <div className={styles.paymentContainer}>
              <div className={styles.paymentHeader}>
                <button className={styles.backBtn} onClick={handleBackToPlans}>
                  ← Volver
                </button>
                <h3>Pago con tarjeta - {selectedPlan.name}</h3>
              </div>
              
              <MpForm
                selectedPlan={selectedPlan}
                onStartPayment={handleStartPayment}
                onReady={handlePaymentReady}
              />
            </div>
          )}

          {currentView === 'transfer' && (
            <TransferView
              amount={transferAmount}
              onBack={handleBackToPlans}
            />
          )}

          {currentView === 'result' && paymentResult && (
            <ResultView
              isSuccess={paymentResult.isSuccess}
              isMercadopagoError={paymentResult.isMercadopagoError}
              onClose={handleClose}
            />
          )}
        </motion.div>
      </ModalOverlay>
      {suscriptionLoading && <Loading noModal={false} />}
    </>
  );
};

const mapStateToProps = (state: any) => ({
  userData: state.general.userData,
  suscriptionLoading: state.profile.suscriptionLoading,
  isUserPremium: state.general.isUserPremium,
});

export default connect(mapStateToProps, {
  closeSuscriptionModal,
  getSuscriptionLink,
  loadUserData,
  setSuscriptionLoading,
})(SuscriptionModalNew);

